import React, { useState } from 'react';
import Webcam from 'react-webcam';
import card from '../../assets/images/Generic DL.jpeg'
import Cropper, { ReactCropperElement } from 'react-cropper';
import { GuidanceModal, Slide } from '../../components/Modals/GuidanceModal';
import { useTranslation } from 'react-i18next';

const THEME = '#c67b05';
const THEME_SHADOW = 'rgba(198,123,5,0.32)';


interface IPhotoIDProps {
    imagePhotoID: string | null;
    isPhotoIDCroped: boolean;
    useCamPhotoID: boolean;
    startSelfieCameraPhotoID: () => void;
    onChangePhotoID: (event: React.ChangeEvent<HTMLInputElement>) => void;
    cropperPhotoIdRef: React.RefObject<ReactCropperElement>;
    resetImagePhotoID: () => void;
    handleZoomInPhotoID: () => void;
    handleZoomOutPhotoID: () => void;
    getCropDataPhotoID: () => void;
    webcamPhotoIDRef: React.RefObject<Webcam>;
    captureImageFromWebcamPhotoID: () => void;
    rotateImageID: () => void;
    isMobile: boolean;
    // Desktop portrait/landscape camera props
    idOrientation: 'landscape' | 'portrait';
    onOrientationChange: (o: 'landscape' | 'portrait') => void;
    idZoom: number;
    onZoomChange: React.Dispatch<React.SetStateAction<number>>;
    onCapture: () => void;
    isIDCapturing: boolean;
}

export const PhotoIDUpload = ({
    imagePhotoID,
    isPhotoIDCroped,
    useCamPhotoID,
    startSelfieCameraPhotoID,
    onChangePhotoID,
    cropperPhotoIdRef,
    resetImagePhotoID,
    handleZoomInPhotoID,
    handleZoomOutPhotoID,
    getCropDataPhotoID,
    webcamPhotoIDRef,
    captureImageFromWebcamPhotoID,
    isMobile,
    rotateImageID,
    idOrientation,
    onOrientationChange,
    idZoom,
    onZoomChange,
    onCapture,
    isIDCapturing,
}: IPhotoIDProps) => {
    const isLandscape = idOrientation === 'landscape';
    const { t, i18n } = useTranslation();
    const photoUploadLink = i18n.language === 'Spanish'
        ? 'https://nccco.org/ID-guidance-sp'
        : 'http://www.nccco.org/ID';

    const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
    const [cropOverlay, setCropOverlay] = useState<{ left: number; top: number; width: number; height: number } | null>(null);

    const syncOverlay = () => {
        const cropper = cropperPhotoIdRef.current?.cropper;
        if (!cropper) return;
        const cb = cropper.getCropBoxData();
        setCropOverlay({ left: cb.left, top: cb.top, width: cb.width, height: cb.height });
    };

    const idSlides: Slide[] = [
        {
            title: t('PhotoManagement.PhotoIDUpload.GuidanceStep1Title', 'Acceptable ID'),
            description: t('PhotoManagement.PhotoIDUpload.GuidanceStep1Desc', 'Must be government-issued (e.g., driver\'s license, passport), valid (not expired), and the full photo of the entire ID must show your full legal name matching myCCO and your date of birth.'),
            isAcceptable: true,
            image: card
        },
        {
            title: t('PhotoManagement.PhotoIDUpload.GuidanceStep2Title', 'Unacceptable: Expired or Covered Parts'),
            description: t('PhotoManagement.PhotoIDUpload.GuidanceStep2Desc', 'Expired IDs are not acceptable, nor can any parts of the ID be covered, crossed out, or redacted.'),
            isAcceptable: false,
            image: `${process.env.PUBLIC_URL || ''}/guideline-images/id-no-expired-no-hidden-part.png`
        },
        {
            title: t('PhotoManagement.PhotoIDUpload.GuidanceStep4Title', 'Unacceptable: Work/School ID'),
            description: t('PhotoManagement.PhotoIDUpload.GuidanceStep4Desc', 'Work badges or educational institution IDs are not accepted.'),
            isAcceptable: false,
            image: `${process.env.PUBLIC_URL || ''}/guideline-images/id-no-labour-no-school-card.png`
        },
        {
            title: t('PhotoManagement.PhotoIDUpload.GuidanceStep5Title', 'Unacceptable: Incomplete/Cropped'),
            description: t('PhotoManagement.PhotoIDUpload.GuidanceStep5Desc', 'Image cannot be blurry, poorly lit, or have chopped off corners/edges.'),
            isAcceptable: false,
            image: `${process.env.PUBLIC_URL || ''}/guideline-images/id-incomplete-image.png`
        }
    ];
    return (
        <div style={{ width: '100%' }}>
    
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '20px' }}>

                {/* Modern Dropzone & Webcam Options */}
                <div style={{ display: (imagePhotoID || isPhotoIDCroped) ? 'none' : 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px', alignItems: 'stretch', justifyContent: 'center', maxWidth: '850px', margin: '0 auto', width: '100%' }}>

                    {/* DISABLED — re-enable when needed
                    <label
                        style={{
                            border: '2px dashed #cbd5e1',
                            background: '#f8fafc',
                            borderRadius: '16px',
                            padding: '40px 20px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            flex: 1,
                            minHeight: '220px'
                        }}
                        htmlFor="ccoPhotoID"
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = THEME; e.currentTarget.style.background = '#fff'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.background = '#f8fafc'; }}
                    >
                        <input type='file' id='ccoPhotoID' hidden onChange={onChangePhotoID} />
                        <div style={{ background: '#fff', color: THEME, width: '64px', height: '64px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '18px', boxShadow: '0 4px 14px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                        </div>
                        <span style={{ color: '#1e293b', fontWeight: 700, fontSize: '18px', fontFamily: '"DMSans Bold"' }}>{t('PhotoManagement.PhotoIDUpload.Upload')}</span>
                        <span style={{ color: '#64748b', fontSize: '14px', marginTop: '6px' }}>{t('PhotoManagement.PhotoIDUpload.DragDrop') || 'Click or Drag and Drop here'}</span>
                    </label>

                    <div style={{ display: 'flex', flexDirection: isMobile ? 'row' : 'column', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                        {!isMobile && <div style={{ width: '1px', flex: 1, background: '#e2e8f0' }} />}
                        <span style={{ padding: isMobile ? '0 10px' : '10px 0', color: '#94a3b8', fontSize: '14px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>{t('PhotoManagement.PhotoManagement.OR') || 'OR'}</span>
                        {!isMobile && <div style={{ width: '1px', flex: 1, background: '#e2e8f0' }} />}
                    </div>
                    */}

                    {/* Use Camera Card */}
                    <div
                        onClick={startSelfieCameraPhotoID}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = THEME; e.currentTarget.style.background = '#fff'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.background = '#f8fafc'; }}
                        style={{
                            border: '2px dashed #cbd5e1',
                            background: '#f8fafc',
                            borderRadius: '16px',
                            padding: '40px 20px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            width: '100%',
                            maxWidth: '420px',
                            minHeight: '220px'
                        }}
                    >
                        <div style={{ background: '#fff', color: THEME, width: '64px', height: '64px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '18px', boxShadow: '0 4px 14px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
                        </div>
                        <span style={{ color: '#1e293b', fontWeight: 700, fontSize: '18px', fontFamily: '"DMSans Bold"' }}>{t('PhotoManagement.PhotoIDUpload.Webcam')}</span>
                        <span style={{ color: '#64748b', fontSize: '14px', marginTop: '6px' }}>Capture using live camera</span>
                    </div>

                </div>


                {/* ID Studio Workspace */}
                {imagePhotoID && (
                    <div style={{ 
                        width: "100%", background: '#fff', borderRadius: '24px', 
                        padding: '32px', border: '1px solid #e2e8f0',
                        boxShadow: '0 20px 50px rgba(0,0,0,0.04)',
                        display: 'flex', gap: '32px', flexWrap: 'wrap'
                    }}>
                        {/* Left: Studio Area */}
                        <div style={{ flex: 1.5, minWidth: '460px', position: 'relative' }}>
                            <div style={{ 
                                width: "100%", position: 'relative', overflow: 'hidden', 
                                borderRadius: '20px', background: '#1a1f2e',
                                boxShadow: 'inset 0 0 40px rgba(0,0,0,0.2)'
                            }}>
                                <Cropper
                                    ref={cropperPhotoIdRef}
                                    className="no-mask"
                                    style={{ height: '480px', width: "100%" }}
                                    aspectRatio={1.586}
                                    src={imagePhotoID}
                                    viewMode={0}
                                    cropBoxMovable={true}
                                    background={false}
                                    responsive={true}
                                    cropBoxResizable={true}
                                    autoCropArea={0.88}
                                    dragMode="move"
                                    checkOrientation={false}
                                    guides={true}
                                    ready={() => {
                                        const cropper = cropperPhotoIdRef.current?.cropper;
                                        if (!cropper) return;

                                        const container = cropper.getContainerData();
                                        const image = cropper.getImageData();
                                        const ID_RATIO = 1.586;

                                        // ── 1. Size the crop box to the ID card ratio ──────────
                                        let cbW = container.width * 0.88;
                                        let cbH = cbW / ID_RATIO;
                                        if (cbH > container.height * 0.82) {
                                            cbH = container.height * 0.82;
                                            cbW = cbH * ID_RATIO;
                                        }
                                        const cbLeft = (container.width  - cbW) / 2;
                                        const cbTop  = (container.height - cbH) / 2;
                                        cropper.setCropBoxData({ left: cbLeft, top: cbTop, width: cbW, height: cbH });

                                        // ── 2. Zoom to fit the full image inside the crop box ──
                                        const fitZoom = Math.min(
                                            cbW / image.naturalWidth,
                                            cbH / image.naturalHeight
                                        );
                                        cropper.zoomTo(fitZoom);

                                        // ── 3. Centre canvas and sync overlay ─────────────────
                                        setTimeout(() => {
                                            const cv = cropper.getCanvasData();
                                            cropper.setCanvasData({
                                                left: cbLeft + (cbW - cv.width)  / 2,
                                                top:  cbTop  + (cbH - cv.height) / 2,
                                            });
                                            syncOverlay();
                                        }, 0);
                                    }}
                                    crop={syncOverlay}
                                    cropend={syncOverlay}
                                />

                                {/* Dynamic overlay that tracks the crop box */}
                                {cropOverlay && (
                                    <>
                                        <div style={{
                                            position: 'absolute',
                                            left: cropOverlay.left,
                                            top: cropOverlay.top,
                                            width: cropOverlay.width,
                                            height: cropOverlay.height,
                                            border: '2.5px dashed rgba(255,255,255,0.7)',
                                            borderRadius: '10px',
                                            pointerEvents: 'none',
                                            boxShadow: '0 0 0 2000px rgba(0,0,0,0.38)',
                                            zIndex: 10,
                                            transition: 'left 0.05s, top 0.05s, width 0.05s, height 0.05s',
                                        }} />
                                        <div style={{
                                            position: 'absolute',
                                            left: cropOverlay.left + cropOverlay.width / 2,
                                            top: cropOverlay.top + cropOverlay.height - 30,
                                            transform: 'translateX(-50%)',
                                            background: 'rgba(0,0,0,0.65)',
                                            padding: '4px 14px', borderRadius: '20px',
                                            color: '#fff', fontSize: '11px', fontWeight: 700,
                                            pointerEvents: 'none', zIndex: 11, whiteSpace: 'nowrap',
                                        }}>
                                            PLACE ID CARD WITHIN FRAME
                                        </div>
                                    </>
                                )}
                            </div>
                            
                            <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: THEME }} />
                                <span style={{ color: '#64748b', fontSize: '13px', fontWeight: 500 }}>Ensure name, date of birth, and photo are clearly visible.</span>
                            </div>
                        </div>

                        {/* Right: Studio Controls Pane */}
                        <div style={{ 
                            flex: 1, minWidth: '280px', background: '#f8fafc', 
                            borderRadius: '20px', padding: '24px', border: '1px solid #f1f5f9',
                            display: 'flex', flexDirection: 'column'
                        }}>
                            <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#1e293b', marginBottom: '20px', fontFamily: '"DMSans Bold"' }}>Adjust ID Photo</h3>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
                                {/* Zoom Controls */}
                                <div style={{ background: '#fff', padding: '16px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '12px', letterSpacing: '0.5px' }}>Zoom Level</div>
                                    <div style={{ display: 'flex', gap: '12px' }}>
                                        <button onClick={handleZoomOutPhotoID} style={{ flex: 1, padding: '10px', background: '#f1f5f9', border: 'none', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#475569', fontWeight: 600, transition: 'all 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'} onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line><line x1="8" y1="11" x2="14" y2="11"></line></svg>
                                            Out
                                        </button>
                                        <button onClick={handleZoomInPhotoID} style={{ flex: 1, padding: '10px', background: '#f1f5f9', border: 'none', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#475569', fontWeight: 600, transition: 'all 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'} onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line><line x1="11" y1="8" x2="11" y2="14"></line><line x1="8" y1="11" x2="14" y2="11"></line></svg>
                                            In
                                        </button>
                                    </div>
                                </div>

                                {/* Rotation Control */}
                                <button onClick={rotateImageID} style={{ width: '100%', padding: '14px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', color: '#1e293b', fontWeight: 700, transition: 'all 0.2s' }} onMouseEnter={e => { e.currentTarget.style.borderColor = THEME; e.currentTarget.style.color = THEME; }} onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#1e293b'; }}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"></polyline><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path></svg>
                                    Rotate ID Card
                                </button>

                                {/* Reset Control */}
                                <button onClick={resetImagePhotoID} style={{ width: '100%', padding: '14px', background: 'transparent', border: '1px solid transparent', borderRadius: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', color: '#ef4444', fontWeight: 600, transition: 'all 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
                                    Cancel & Start Over
                                </button>
                            </div>

                            {/* Final Action */}
                            <button onClick={getCropDataPhotoID} style={{ 
                                width: '100%', padding: '18px', background: THEME, 
                                color: '#fff', border: 'none', borderRadius: '18px', 
                                cursor: 'pointer', fontSize: '16px', fontWeight: 800, 
                                marginTop: '24px', boxShadow: `0 8px 16px ${THEME_SHADOW}`,
                                transition: 'all 0.2s'
                            }} onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 12px 20px ${THEME_SHADOW}`; }} onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = `0 8px 16px ${THEME_SHADOW}`; }}>
                                ✅ Confirm ID Selection
                            </button>
                        </div>
                    </div>
                )}

            </div>
            {/* Post-crop Actions & Guidance */}
            <div style={{ marginTop: '20px' }}>
                {isMobile && !imagePhotoID && (
                    <div style={{ textAlign: 'center', margin: '20px 0' }}>
                        <img src={card} width={180} alt="Sample ID" style={{ borderRadius: '8px', opacity: 0.8 }} />
                    </div>
                )}

                <div style={{ marginTop: '10px' }}>
                    {isMobile && (
                        <a
                            href="#"
                            onClick={(e) => { e.preventDefault(); setIsGuidanceOpen(true); }}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#d19b3d', fontWeight: 600, textDecoration: 'none' }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                            {t('PhotoManagement.PhotoUpload.Submit')}
                        </a>
                    )}
                </div>

                <GuidanceModal isOpen={isGuidanceOpen} onClose={() => setIsGuidanceOpen(false)} slides={idSlides} isRectanglePlaceholder={true} hideUnacceptableBadge={true} />
            </div>



            {useCamPhotoID && !isMobile && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(5px)' }}>
                    <div style={{ background: '#1a1f2e', borderRadius: 16, padding: 24, width: '95%', maxWidth: 720, display: 'flex', flexDirection: 'column', gap: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }}>

                        {/* Orientation toggle */}
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <div style={{ display: 'inline-flex', background: 'rgba(255,255,255,0.08)', borderRadius: 10, padding: 4, gap: 4 }}>
                                {(['landscape', 'portrait'] as const).map(ori => (
                                    <button
                                        key={ori}
                                        onClick={() => onOrientationChange(ori)}
                                        style={{
                                            padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14,
                                            background: idOrientation === ori ? THEME : 'transparent',
                                            color: idOrientation === ori ? '#fff' : 'rgba(255,255,255,0.55)',
                                            transition: 'all 0.2s',
                                        }}
                                    >
                                        {ori === 'landscape' ?  t('PhotoManagement.idCamera.landscape') : t('PhotoManagement.idCamera.portrait')}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Camera + SVG mask */}
                        <div className="pm-cam-container" style={{ position: 'relative', width: '100%', borderRadius: 12, overflow: 'visible', background: '#000', aspectRatio: isLandscape ? '4/3' : '3/4', maxHeight: 500, maxWidth: isLandscape ? undefined : 375, margin: '0 auto' }}>
                            <Webcam
                                audio={false}
                                ref={webcamPhotoIDRef}
                                screenshotFormat="image/jpeg"
                                screenshotQuality={1}
                                mirrored={true}
                                videoConstraints={{ facingMode: { ideal: 'environment' }, width: { ideal: 3840, min: 1280 }, height: { ideal: 2160, min: 720 } }}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', transform: `scale(${idZoom})`, transformOrigin: 'center' }}
                            />

                      

                            {/* Dashed ID mask SVG */}
                            <svg
                                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                                viewBox={isLandscape ? '0 0 133 100' : '0 0 100 133'}
                                preserveAspectRatio="xMidYMid meet"
                            >
                                {isLandscape ? (
                                    <>
                                        <defs>
                                            <mask id="id-l-m-d">
                                                <rect width="133" height="100" fill="white" />
                                                <rect x="13" y="15" width="107" height="70" rx="4" fill="black" />
                                            </mask>
                                        </defs>
                                        <rect width="133" height="100" fill="rgba(0,0,0,0.48)" mask="url(#id-l-m-d)" />
                                        <rect x="13" y="15" width="107" height="70" rx="4" fill="none" stroke="rgba(255,255,255,0.88)" strokeWidth="0.8" strokeDasharray="4 2.5" />
                                    </>
                                ) : (
                                    <>
                                        <defs>
                                            <mask id="id-p-m-d">
                                                <rect width="100" height="133" fill="white" />
                                                <rect x="12" y="13" width="76" height="107" rx="4" fill="black" />
                                            </mask>
                                        </defs>
                                        <rect width="100" height="133" fill="rgba(0,0,0,0.48)" mask="url(#id-p-m-d)" />
                                        <rect x="12" y="13" width="76" height="107" rx="4" fill="none" stroke="rgba(255,255,255,0.88)" strokeWidth="0.8" strokeDasharray="4 2.5" />
                                    </>
                                )}
                            </svg>

                            <span style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 12, fontWeight: 600, padding: '4px 14px', borderRadius: 20, whiteSpace: 'nowrap', pointerEvents: 'none' }}>
                                {t('PhotoManagement.idCamera.alignHint')}
                            </span>
                        </div>

                        {/* Action buttons */}
                        <div style={{ display: 'flex', gap: 12 }}>
                            <button
                                onClick={resetImagePhotoID}
                                style={{ flex: 1, padding: '12px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 15, transition: 'background 0.2s' }}
                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                            >
                                {t('PhotoManagement.PhotoIDUpload.Close')}
                            </button>
                            <button
                                onClick={onCapture}
                                disabled={isIDCapturing}
                                style={{ flex: 1, padding: '12px', background: isIDCapturing ? '#888' : THEME, border: 'none', borderRadius: 10, color: '#fff', fontWeight: 700, cursor: isIDCapturing ? 'not-allowed' : 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'background 0.2s', boxShadow: `0 4px 14px ${THEME_SHADOW}` }}
                                onMouseEnter={e => { if (!isIDCapturing) e.currentTarget.style.background = '#b88133'; }}
                                onMouseLeave={e => { if (!isIDCapturing) e.currentTarget.style.background = THEME; }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="13" r="4"/><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/></svg>
                                {isIDCapturing ? 'Capturing…' : t('PhotoManagement.PhotoIDUpload.Capture')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div >
    );
};


